import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "@/routes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Clipboard,
  Calendar,
  Activity,
  Heart,
  FileText,
  AlertCircle,
  Pill,
  Stethoscope,
  Clock,
  Video,
  Target,
  Send,
  Loader2,
  HeartHandshake,
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import { PatientDemographics } from "@/components/patient/PatientDemographics";
import { PatientMedicalInfo } from "@/components/patient/PatientMedicalInfo";
import { PatientAppointments } from "@/components/patient/PatientAppointments";
import { PatientCarePlans } from "@/components/patient/PatientCarePlans";
import { PatientIntakeForm } from "@/components/patient/PatientIntakeForm";
import { PatientNotes } from "@/components/patient/PatientNotes";
import { PatientSdohSummary } from "@/components/patient/PatientSdohSummary";

export default function PatientDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const defaultTab = searchParams.get("tab") || "demographics";
  const queryClient = useQueryClient();
  const [quickNote, setQuickNote] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  const handleQuickNoteSave = async () => {
    if (!quickNote.trim() || !user || !patientId) return;
    setIsSavingNote(true);
    try {
      const { error } = await supabase.from("patient_notes").insert({
        patient_id: patientId,
        provider_id: user.id,
        note_text: quickNote.trim(),
        services: [],
        cpt_codes: [],
        billing_synced: false,
      });
      if (error) throw error;
      toast.success("Note saved successfully");
      setQuickNote("");
      queryClient.invalidateQueries({ queryKey: ["patient-notes", patientId] });
    } catch (err: any) {
      toast.error(`Failed to save note: ${err.message}`);
    } finally {
      setIsSavingNote(false);
    }
  };

  // Fetch patient intake form data
  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ["patient-detail", patientId],
    queryFn: async () => {
      if (!patientId) return null;

      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .eq("user_id", patientId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!patientId,
  });

  // Fetch patient vitals
  const { data: vitals } = useQuery({
    queryKey: ["patient-vitals", patientId],
    queryFn: async () => {
      if (!patientId) return null;

      const { data, error } = await supabase
        .from("patient_vitals")
        .select("*")
        .eq("member_id", patientId)
        .order("recorded_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!patientId,
  });

  // Fetch appointments
  const { data: appointments } = useQuery({
    queryKey: ["patient-appointments", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("member_id", patientId)
        .order("scheduled_date", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    },
    enabled: !!patientId,
  });

  // Fetch care plans
  const { data: carePlans } = useQuery({
    queryKey: ["patient-care-plans", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("care_plans")
        .select(`
          *,
          care_plan_goals (*)
        `)
        .eq("member_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!patientId,
  });

  // Fetch health alerts
  const { data: alerts } = useQuery({
    queryKey: ["patient-alerts", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("health_alerts")
        .select("*")
        .eq("member_id", patientId)
        .eq("is_acknowledged", false)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    },
    enabled: !!patientId,
  });

  if (patientLoading) {
    return (
      <div className="flex min-h-screen bg-background">
        <div className="hidden lg:block">
          <ProviderSidebar />
        </div>
        <div className="flex-1 flex flex-col lg:ml-64">
          <ProviderHeader />
          <main className="flex-1 p-4 md:p-6">
            <div className="space-y-6">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="flex min-h-screen bg-background">
        <div className="hidden lg:block">
          <ProviderSidebar />
        </div>
        <div className="flex-1 flex flex-col lg:ml-64">
          <ProviderHeader />
          <main className="flex-1 p-4 md:p-6">
            <div className="text-center py-12">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Patient Not Found</h2>
              <p className="text-muted-foreground mb-4">
                This patient may not have an active session with you.
              </p>
              <Button onClick={() => navigate(ROUTES.PROVIDER_CASES)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Cases
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const patientName = `${patient.first_name} ${patient.last_name}`;
  const initials = `${patient.first_name?.[0] || ""}${patient.last_name?.[0] || ""}`.toUpperCase();
  const age = patient.date_of_birth
    ? Math.floor((new Date().getTime() - new Date(patient.date_of_birth).getTime()) / 31557600000)
    : null;

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden lg:block">
        <ProviderSidebar />
      </div>
      <div className="flex-1 flex flex-col lg:ml-64">
        <ProviderHeader />
        <main className="flex-1 p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="self-start">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
              <Avatar className="h-12 w-12 sm:h-16 sm:w-16 shrink-0">
                <AvatarImage src={patient.patient_photo_url || undefined} />
                <AvatarFallback className="text-base sm:text-lg bg-primary/10 text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold truncate">{patientName}</h1>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  {age && <span>{age} years old</span>}
                  <span className="hidden sm:inline">•</span>
                  <Badge variant="outline" className="capitalize">
                    {patient.insurance_type?.replace("_", " ")}
                  </Badge>
                  {patient.status && (
                    <Badge
                      variant={patient.status === "completed" ? "default" : "secondary"}
                    >
                      Intake {patient.status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 self-start sm:self-center">
              <Button variant="outline" size="sm" onClick={() => {
                const tabEl = document.querySelector('[data-state="inactive"][value="notes"]') as HTMLElement;
                tabEl?.click();
              }}>
                <Clipboard className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">View Notes</span>
              </Button>
              <Button size="sm">
                <Video className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Start Session</span>
              </Button>
            </div>
          </div>

          {/* Alerts Banner */}
          {alerts && alerts.length > 0 && (() => {
            const clinicalAlerts = alerts.filter(a => a.alert_type !== 'sdoh');
            const sdohAlerts = alerts.filter(a => a.alert_type === 'sdoh');
            return (
              <div className="mb-6 space-y-3">
                {clinicalAlerts.length > 0 && (
                  <Card className="border-destructive/50 bg-destructive/5">
                    <CardContent className="py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                        <span className="font-medium text-destructive">
                          {clinicalAlerts.length} Clinical Alert{clinicalAlerts.length > 1 ? "s" : ""}
                        </span>
                        <Separator orientation="vertical" className="h-5 hidden sm:block" />
                        <div className="flex-1 text-sm text-muted-foreground">
                          {clinicalAlerts.slice(0, 3).map((alert, i) => (
                            <span key={alert.id}>
                              {i > 0 && " • "}
                              {alert.metric}: {alert.value}
                            </span>
                          ))}
                          {clinicalAlerts.length > 3 && (
                            <span className="text-xs ml-1">(+{clinicalAlerts.length - 3} more)</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {sdohAlerts.length > 0 && (
                  <Card className="border-orange-300 bg-orange-50/50 dark:bg-orange-950/10 dark:border-orange-800">
                    <CardContent className="py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <HeartHandshake className="h-5 w-5 text-orange-600 dark:text-orange-400 shrink-0" />
                        <span className="font-medium text-orange-700 dark:text-orange-300">
                          {sdohAlerts.length} Social Need{sdohAlerts.length > 1 ? "s" : ""} Flagged
                        </span>
                        <Separator orientation="vertical" className="h-5 hidden sm:block" />
                        <div className="flex-1 flex flex-wrap gap-1.5">
                          {sdohAlerts.map((alert) => (
                            <Badge
                              key={alert.id}
                              variant="outline"
                              className="text-xs border-orange-300 bg-orange-100 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-700"
                            >
                              {alert.metric}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* SDOH Screening Summary */}
          {patient.sdoh_screenings && (patient.sdoh_screenings as string[]).length > 0 && (
            <div className="mb-6">
              <PatientSdohSummary
                sdohScreenings={patient.sdoh_screenings as string[]}
                sdohNotes={(patient as any).sdoh_notes}
                memberId={patientId}
              />
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Blood Pressure</p>
                    <p className="font-semibold">
                      {vitals?.systolic_bp && vitals?.diastolic_bp
                        ? `${vitals.systolic_bp}/${vitals.diastolic_bp}`
                        : "—"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Heart Rate</p>
                    <p className="font-semibold">
                      {vitals?.heart_rate ? `${vitals.heart_rate} bpm` : "—"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Target className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Goals</p>
                    <p className="font-semibold">
                      {carePlans?.reduce((acc, cp) => acc + (cp.care_plan_goals?.length || 0), 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Sessions</p>
                    <p className="font-semibold">{appointments?.length || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Note Section */}
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Textarea
                    placeholder="Add a quick note about this patient..."
                    value={quickNote}
                    onChange={(e) => setQuickNote(e.target.value)}
                    className="min-h-[60px] text-sm resize-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        handleQuickNoteSave();
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Press ⌘+Enter to save quickly
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleQuickNoteSave}
                  disabled={isSavingNote || !quickNote.trim()}
                  className="mt-1"
                >
                  {isSavingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><Send className="h-4 w-4 mr-1" /> Save Note</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue={defaultTab} className="space-y-4">
            <TabsList className="w-full overflow-x-auto flex-wrap h-auto gap-1">
              <TabsTrigger value="demographics">
                <User className="h-4 w-4 mr-2" />
                Demographics
              </TabsTrigger>
              <TabsTrigger value="medical">
                <Stethoscope className="h-4 w-4 mr-2" />
                Medical
              </TabsTrigger>
              <TabsTrigger value="appointments">
                <Calendar className="h-4 w-4 mr-2" />
                Appointments
              </TabsTrigger>
              <TabsTrigger value="care-plans">
                <Target className="h-4 w-4 mr-2" />
                Care Plans
              </TabsTrigger>
              <TabsTrigger value="intake-form">
                <FileText className="h-4 w-4 mr-2" />
                Intake Form
              </TabsTrigger>
              <TabsTrigger value="notes">
                <Clipboard className="h-4 w-4 mr-2" />
                Notes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="demographics">
              <PatientDemographics patient={patient} patientId={patientId} />
            </TabsContent>

            <TabsContent value="medical">
              <PatientMedicalInfo patient={patient} vitals={vitals} />
            </TabsContent>

            <TabsContent value="appointments">
              <PatientAppointments appointments={appointments || []} patientId={patientId!} />
            </TabsContent>

            <TabsContent value="care-plans">
              <PatientCarePlans carePlans={carePlans || []} patientId={patientId!} />
            </TabsContent>

            <TabsContent value="intake-form">
              <PatientIntakeForm patient={patient} />
            </TabsContent>

            <TabsContent value="notes">
              <PatientNotes patientId={patientId!} patientName={patientName} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
